import { generateJWT } from './jwt';

interface HttpClientConfig {
  baseURL?: string;
  headers?: Record<string, string>;
  timeout?: number;
  useJWT?: boolean;
}

interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
}

class HttpClient {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;
  private timeout: number;
  private useJWT: boolean;

  constructor(config: HttpClientConfig = {}) {
    this.baseURL = config.baseURL || '';
    this.useJWT = config.useJWT !== false;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.headers,
    };
    this.timeout = config.timeout || 30000;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    requestOptions?: RequestOptions
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    let jwtToken: string | null = null;
    if (this.useJWT) {
      jwtToken = await generateJWT();
    }
    
    const mergedHeaders = {
      ...this.defaultHeaders,
      ...(jwtToken && { Authorization: `Bearer ${jwtToken}` }),
      ...requestOptions?.headers,
      ...options.headers,
    };
    
    const headers: Record<string, string> = {};
    Object.keys(mergedHeaders).forEach(key => {
      if (mergedHeaders[key] !== '') {
        headers[key] = mergedHeaders[key];
      }
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), requestOptions?.timeout || this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        return response.text() as unknown as T;
      }

      return await response.json();
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  async get<T>(endpoint: string, requestOptions?: RequestOptions): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'GET',
      },
      requestOptions
    );
  }

  async post<T>(
    endpoint: string,
    data?: any,
    requestOptions?: RequestOptions
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      requestOptions
    );
  }

  async put<T>(
    endpoint: string,
    data?: any,
    requestOptions?: RequestOptions
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      },
      requestOptions
    );
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    requestOptions?: RequestOptions
  ): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'PATCH',
        body: data ? JSON.stringify(data) : undefined,
      },
      requestOptions
    );
  }

  async delete<T>(endpoint: string, data?: any, requestOptions?: RequestOptions): Promise<T> {
    return this.makeRequest<T>(
      endpoint,
      {
        method: 'DELETE',
        body: data ? JSON.stringify(data) : undefined,
        headers: data ? undefined : { 'Content-Type': '' },
      },
      requestOptions
    );
  }

  setHeader(key: string, value: string) {
    this.defaultHeaders[key] = value;
  }

  removeHeader(key: string) {
    delete this.defaultHeaders[key];
  }
}

export default HttpClient;

